import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";

const RED = "#CC2222";
const CHARCOAL = "#1A1A1A";
const BODY = "#2C2825";
const LIGHT = "#F5F5F5";

const TIMELINE = [
  {
    bg: "#FFFFFF",
    label: "The Beginning · Cuba",
    heading: "A Question That Changed Everything",
    body: "It all started in Cuba, at nineteen years old. A friend looked over and asked a simple question — 'Do you know how to cut hair?' The honest answer was no. But something inside said: we can try. That afternoon, with nothing but curiosity and a willing friend in the chair, a calling was born. Word spread quickly. Neighbors, friends, anyone who wanted a fresh cut came knocking — and not one of them left disappointed.",
    sublabel: "First Clippers · First Lessons",
    subheading: "Learning the Craft",
    subbody: "The first real trimmer. The first pair of scissors. A seat in a barbershop alongside a mentor who gave freely of his knowledge — teaching every technique, every line, every fade, step by step. Confidence grew with every client. Within months, professional styles were within reach, and the fire to grow had only just been lit.",
    image: "https://images.unsplash.com/photo-1599351431202-1e0f0137899a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    bg: LIGHT,
    label: "Expanding · Barbershops & Salons",
    heading: "When the Women Asked",
    body: "Moving through different barbershops and hair salons, the reputation grew. One day, the salon's female clients watched the work and asked: 'Can you cut my hair too?' The answer was always yes. That moment of trust sparked a new chapter — formal hairstylist courses in color, blowdry, highlights, and advanced styling. A big step. A bigger dream.",
    image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    bg: "#FFFFFF",
    label: "Age 30 · Spain · The Real School",
    heading: "A Master and a Best Friend",
    body: "At thirty, Spain became the greatest classroom. There, a mentor named David Rodelas — chef and best friend — taught something beyond technique. How to truly connect with a client. How to lead other stylists. How to elevate every single cut into an experience. Spain didn't just sharpen the scissors — it shaped the philosophy.",
    image: "https://images.unsplash.com/photo-1622286342621-4bd786c2447c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    bg: LIGHT,
    label: "Age 38 · Miami, Florida",
    heading: "America, and the Stars",
    body: "At thirty-eight, after eight transformative years in Spain, the next horizon called: the United States. Miami — Doral — a vibrant salon where the chairs were filled with remarkable people. Artists. Entertainers. Familiar faces like Chyno y Nacho, Juan Galavís, Karim Mendiburo, and more. Every appointment was a reminder: great hair transcends language, culture, and borders.",
    image: "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    bg: "#FFFFFF",
    label: "2016 · Our Own Salon",
    heading: "A Dream Built for Two",
    body: "Two and a half years in, the idea became a conversation. 'Do you want to come with me? I'll teach you everything I know.' His wife said yes — and she didn't just learn. She surpassed. Today, she is one of the finest colorists and women's hairstylists in the salon. In 2016, AXtreme Hair Style opened its doors in Miami, Florida — built on love, craft, and an unshakable dream of making every guest feel at home.",
    image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

function TimelineBlock({ entry, index }) {
  const reverse = index % 2 === 1;
  return (
    <section className="py-20 sm:py-24" style={{ background: entry.bg }} data-testid={`timeline-block-${index}`}>
      <div className={`max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "md:[&>div:first-child]:order-2" : ""}`}>
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10" style={{ background: RED }} />
            <span className="text-[10px] tracking-[0.4em] uppercase font-semibold" style={{ color: RED }}>
              {entry.label}
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl leading-[1.05]" style={{ color: CHARCOAL, fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            {entry.heading}
          </h2>
          <p className="mt-6 leading-relaxed" style={{ color: BODY }}>
            {entry.body}
          </p>
          {entry.subheading && (
            <div className="mt-10">
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-8" style={{ background: RED }} />
                <span className="text-[10px] tracking-[0.4em] uppercase font-semibold" style={{ color: RED }}>
                  {entry.sublabel}
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl" style={{ color: CHARCOAL, fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {entry.subheading}
              </h3>
              <p className="mt-4 leading-relaxed" style={{ color: BODY }}>
                {entry.subbody}
              </p>
            </div>
          )}
        </div>
        <div className="w-full aspect-[4/5] overflow-hidden border border-[#E0E0E0]" style={{ background: "#FFFFFF" }}>
          <img src={entry.image} alt={entry.heading} className="w-full h-full object-cover" />
        </div>
      </div>
    </section>
  );
}

export default function OurStory() {
  return (
    <div data-testid="our-story-page" style={{ background: "#FFFFFF" }}>
      {/* Page hero */}
      <section className="py-24 sm:py-32" style={{ background: LIGHT, borderBottom: `1px solid ${RED}` }} data-testid="our-story-hero">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10" style={{ background: RED }} />
            <span className="text-[11px] tracking-[0.4em] uppercase font-semibold" style={{ color: RED }}>Our Story</span>
            <span className="h-px w-10" style={{ background: RED }} />
          </div>
          <h1 className="text-6xl sm:text-7xl lg:text-8xl leading-[1.02]" style={{ color: CHARCOAL, fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            The Journey
          </h1>
          <p className="mt-6 text-lg sm:text-xl italic" style={{ color: BODY, fontFamily: "'Cormorant Garamond', serif" }}>
            From Cuba to Miami — a story built on passion, craft and love
          </p>
        </div>
      </section>

      {/* Timeline */}
      {TIMELINE.map((entry, i) => (
        <Reveal key={entry.label}><TimelineBlock entry={entry} index={i} /></Reveal>
      ))}

      {/* Closing red banner */}
      <Reveal>
        <section className="py-24 sm:py-28 text-center" style={{ background: RED, color: "#FFFFFF" }} data-testid="our-story-closing">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-[11px] tracking-[0.4em] uppercase font-semibold opacity-90">Today</div>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Growing — Professionally & Humanly
            </h2>
            <p className="mt-8 text-base sm:text-lg leading-relaxed opacity-95 max-w-2xl mx-auto">
              Today, AXtreme Hair Style is more than a salon. It is a brand — with its own line of men's and women's hair care products, a team that leads with heart, and a mission that puts God first. The dream keeps growing. The scissors keep moving. And every guest who sits in our chair becomes part of this story.
            </p>
            <div className="my-12 mx-auto h-px w-16 bg-white/40" />
            <blockquote className="text-2xl sm:text-3xl lg:text-4xl italic leading-snug max-w-3xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              "Every guest who sits in our chair becomes part of this story."
            </blockquote>
          </div>
        </section>
      </Reveal>

      {/* CTA */}
      <Reveal>
        <section className="py-20 text-center" style={{ background: "#FFFFFF" }}>
          <div className="max-w-2xl mx-auto px-6 lg:px-8">
            <h3 className="text-3xl sm:text-4xl" style={{ color: CHARCOAL, fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Be part of the next chapter.
            </h3>
            <a
              href="https://www.vagaro.com/axtreme/services"
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-block px-12 py-4 text-xs tracking-[0.3em] uppercase text-white bg-[#CC2222] hover:bg-[#a81b1b] transition-colors"
              data-testid="our-story-book-cta"
            >
              Book Your Visit
            </a>
            <div className="mt-6">
              <Link to="/contact" className="text-xs tracking-[0.3em] uppercase opacity-70 hover:opacity-100" style={{ color: CHARCOAL }}>Visit the Studio →</Link>
            </div>
          </div>
        </section>
      </Reveal>
    </div>
  );
}
